import { Controller } from "@hotwired/stimulus"

// Drives the signature panel rendered by app/views/shared/document_blocks/
// _signature.html.erb in :fill context.
//
// Two modes:
//  - draw: canvas captures pen/mouse/touch strokes; the resulting PNG
//    data URL is written to the hidden signature_data input on each stroke.
//  - type: a text input + italic preview; the typed name posts as
//    signature_text with signature_type=type_to_sign.
//
// Mode toggle clears the other mode's data so submission carries exactly
// one signature payload.
export default class extends Controller {
  static targets = [
    "canvas", "canvasWrapper", "input", "textInput",
    "clearButton", "modeToggle", "typeInput", "error"
  ]
  static values = { mode: { type: String, default: "draw" } }

  connect() {
    this.isEmpty = true
    this.drawing = false
    this.setupCanvas()
    this.bindEvents()
    this.updateUI()

    // Re-size the canvas if it ever becomes visible after being hidden
    // (e.g., the form was rendered inside an initially-hidden Turbo Frame).
    this.resizeObserver = new ResizeObserver(() => this.fitCanvasToWrapper())
    if (this.hasCanvasWrapperTarget) {
      this.resizeObserver.observe(this.canvasWrapperTarget)
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    // Tear down window handlers if a stroke was mid-flight when the
    // controller is removed (Turbo navigation, modal close, etc.).
    this.detachWindowMouseHandlers()
  }

  setupCanvas() {
    // ctx must be set BEFORE fitCanvasToWrapper, because fit calls
    // applyBrushStyle which dereferences this.ctx.
    this.ctx = this.canvasTarget.getContext("2d")
    this.fitCanvasToWrapper()
    this.applyBrushStyle()
  }

  applyBrushStyle() {
    if (!this.ctx) return
    this.ctx.strokeStyle = "#1f2937"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
  }

  // Sets the canvas backing-store size to match its CSS-rendered width.
  // Without this, drawing coordinates drift from cursor position because
  // the canvas's intrinsic width (default 300) doesn't match its rendered
  // width (whatever the wrapper's flexible width is).
  fitCanvasToWrapper() {
    if (!this.hasCanvasWrapperTarget) return
    if (!this.ctx) return // setup not finished yet
    const rect = this.canvasWrapperTarget.getBoundingClientRect()
    if (rect.width === 0) return // not laid out yet — observer will retry
    // Subtract the wrapper's 1px border on each side so the buffer width
    // matches the canvas's CSS-rendered width (cursor coords stay accurate).
    const targetWidth = Math.max(0, Math.floor(rect.width) - 2)
    if (this.canvasTarget.width === targetWidth) return
    // Preserve any existing strokes when resizing
    const previous = this.isEmpty ? null : this.canvasTarget.toDataURL("image/png")
    // Only touch width — height is locked via CSS h-[150px] and the
    // buffer height attribute. Writing canvas.height (even to the same
    // value) resets the context state and forces a repaint.
    this.canvasTarget.width = targetWidth
    // Setting canvas.width resets the context — re-apply brush state.
    this.applyBrushStyle()
    if (previous) {
      const img = new Image()
      img.onload = () => this.ctx.drawImage(img, 0, 0)
      img.src = previous
    }
  }

  bindEvents() {
    const canvas = this.canvasTarget

    // Mouse: down on canvas starts a stroke; move/up are bound at the
    // window level while drawing so the stroke survives leaving the
    // canvas. Returning to the canvas with the button still held picks
    // the stroke back up. Without window listeners, mouse events stop
    // firing the moment the cursor crosses the canvas boundary.
    canvas.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return // ignore right/middle click
      this.startDrawing(this.canvasOffset(e))
    })

    // Touch: touchmove on a captured touch keeps firing on the original
    // target (canvas) even when the finger moves off the canvas, so we
    // can keep canvas-level handlers here.
    canvas.addEventListener("touchstart", (e) => {
      e.preventDefault()
      this.startDrawing(this.canvasOffset(e.touches[0]))
    }, { passive: false })
    canvas.addEventListener("touchmove", (e) => {
      e.preventDefault()
      this.draw(this.canvasOffset(e.touches[0]))
    }, { passive: false })
    canvas.addEventListener("touchend", () => this.stopDrawing())
    canvas.addEventListener("touchcancel", () => this.stopDrawing())
  }

  // Normalizes mouse and touch events to {offsetX, offsetY} relative to
  // the canvas's current rendered position. Coords can be negative or
  // beyond canvas dimensions when the pointer is outside the canvas;
  // draw() filters those out without ending the stroke.
  canvasOffset(eventOrTouch) {
    const rect = this.canvasTarget.getBoundingClientRect()
    return {
      offsetX: eventOrTouch.clientX - rect.left,
      offsetY: eventOrTouch.clientY - rect.top
    }
  }

  inBounds({ offsetX, offsetY }) {
    return offsetX >= 0 && offsetY >= 0 &&
           offsetX <= this.canvasTarget.width && offsetY <= this.canvasTarget.height
  }

  startDrawing(point) {
    this.drawing = true
    this.outOfBounds = !this.inBounds(point)
    this.ctx.beginPath()
    if (!this.outOfBounds) {
      this.ctx.moveTo(point.offsetX, point.offsetY)
    }
    this.attachWindowMouseHandlers()
  }

  draw(point) {
    if (!this.drawing) return

    if (!this.inBounds(point)) {
      // Pointer left the canvas — flag it so the next in-bounds point
      // restarts the path at the re-entry coordinate instead of drawing
      // a straight line across the gap from the last in-canvas point.
      this.outOfBounds = true
      return
    }

    if (this.outOfBounds) {
      // Re-entering. Move the pen without drawing so the stroke picks
      // up from the new boundary point.
      this.ctx.beginPath()
      this.ctx.moveTo(point.offsetX, point.offsetY)
      this.outOfBounds = false
      return
    }

    this.ctx.lineTo(point.offsetX, point.offsetY)
    this.ctx.stroke()
    this.isEmpty = false
    this.updateClearButton()
  }

  stopDrawing() {
    if (!this.drawing) return
    this.drawing = false
    this.outOfBounds = false
    this.detachWindowMouseHandlers()
    if (!this.isEmpty) {
      this.inputTarget.value = this.canvasTarget.toDataURL("image/png")
      this.hideError()
    }
  }

  // Window-level mouse handlers — only attached while a stroke is in
  // progress so we don't pay listener cost otherwise.
  attachWindowMouseHandlers() {
    if (this._windowMove) return // already attached
    this._windowMove = (e) => this.draw(this.canvasOffset(e))
    this._windowUp = () => this.stopDrawing()
    window.addEventListener("mousemove", this._windowMove)
    window.addEventListener("mouseup", this._windowUp)
  }

  detachWindowMouseHandlers() {
    if (this._windowMove) {
      window.removeEventListener("mousemove", this._windowMove)
      this._windowMove = null
    }
    if (this._windowUp) {
      window.removeEventListener("mouseup", this._windowUp)
      this._windowUp = null
    }
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height)
    this.inputTarget.value = ""
    this.isEmpty = true
    this.updateClearButton()
  }

  switchMode() {
    this.modeValue = (this.modeValue === "draw") ? "type" : "draw"
    if (this.hasTypeInputTarget) {
      this.typeInputTarget.value = (this.modeValue === "draw") ? "draw" : "type_to_sign"
    }
    // Switching modes clears the other mode's data so submission carries
    // exactly one payload (signature_data OR signature_text, not both).
    if (this.modeValue === "draw") {
      if (this.hasTextInputTarget) this.textInputTarget.value = ""
    } else {
      this.clear()
    }
    this.updateUI()
    // Refit the canvas in case wrapper dimensions changed when hidden
    if (this.modeValue === "draw") {
      requestAnimationFrame(() => this.fitCanvasToWrapper())
    }
  }

  updateUI() {
    const isDraw = this.modeValue === "draw"

    if (this.hasCanvasWrapperTarget) {
      this.canvasWrapperTarget.classList.toggle("hidden", !isDraw)
    }
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", !isDraw)
    }
    if (this.hasTextInputTarget) {
      this.textInputTarget.classList.toggle("hidden", isDraw)
      if (!isDraw) this.textInputTarget.focus()
    }
    if (this.hasModeToggleTarget) {
      this.modeToggleTarget.textContent = isDraw ? "Type instead" : "Draw instead"
    }
  }

  updateClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = this.isEmpty
    }
  }

  textChanged() {
    const text = this.textInputTarget.value
    if (text.trim().length > 0) this.hideError()
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
